import React, { useCallback, useEffect, useContext, useRef } from "react";

import { Breadcrumb, Button, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { RoleContext, useIsReviewer } from "../context/RoleContext";
import DfnsService from "../services/DfnsService";
import { isAlphanumericAndSpace } from "../utils";

function CreateClaimTopic({ service }) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = React.useState("");
  // Null until the next ID loads, so a save can't go out with a guessed ID.
  const [hiddenName, setHiddenName] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // The ref blocks a second click before the re-render disables the button.
  const savingRef = useRef(false);
  const { user, dfnsToken } = useContext(RoleContext);
  const isReviewer = useIsReviewer();

  const getNextClaimTopicId = useCallback(async () => {
    try {
      const nextClaimTopicId = await service.getNextClaimTopicId();
      setHiddenName(nextClaimTopicId);
      setDisplayName("");
    } catch (e) {
      console.error("Failed get next compliance rule ID:", e);
      toast.error("Failed to load initial data");
    }
  }, [service]);

  function validateClaimTopic(displayName) {
    if (displayName.trim() === "") {
      toast.error("Display Name is required");
      return false;
    }

    if (!isAlphanumericAndSpace(displayName)) {
      toast.error("Display Name must contain only alphanumeric characters");
      return false;
    }

    return true;
  }

  const saveClaimTopic = async () => {
    if (savingRef.current) return;
    const trimmedDisplayName = displayName.trim();

    if (!validateClaimTopic(trimmedDisplayName)) {
      return;
    }

    if (hiddenName == null) {
      toast.error("The compliance rule ID hasn't loaded yet. Please wait or reload the page.");
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      // Handle MANAGED wallet preference using DFNSService
      await toast
        .promise(
          (async () => {
            const { initiateResponse, error: initError } = await DfnsService.initiateAddClaimTopic(
              hiddenName,
              trimmedDisplayName,
              user.walletId,
              dfnsToken
            );
            if (initError) throw new Error(initError);

            const { error: completeError } = await DfnsService.completeAddClaimTopic(
              user.walletId,
              dfnsToken,
              initiateResponse.challenge,
              initiateResponse.requestBody
            );
            if (completeError) throw new Error(completeError);

            navigate("/topics", { state: { newTopic: { topic: String(hiddenName), displayName: trimmedDisplayName } } });
          })(),
          {
            pending: "Creating Compliance Rule...",
            success: `Successfully created ${displayName} compliance rule`,
            error: {
              render({ data }) {
                const msg =
                  data?.reason ||
                  data?.message ||
                  (typeof data === "string" ? data : null) ||
                  `An error occurred while creating Compliance Rule ${displayName}`;
                return <div>{msg}</div>;
              },
            },
          }
        )
        .catch((error) => {
          console.error("Error after attempting to create compliance rule:", error);
        });
    } catch (error) {
      console.error("Unexpected error during compliance rule creation:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  useEffect(() => {
    void getNextClaimTopicId();
  }, [service, getNextClaimTopicId]);

  return (
    <div>
      <Breadcrumb
        className="bg-transparent"
        items={[{ title: <Link to={"/"}>Home</Link> }, { title: <Link to={"/topics"}>Compliance Rules</Link> }, { title: "Add" }]}
      />
      <p className="text-xl p-6">Create Compliance Rule</p>
      <hr />
      <div className="p-3 mt-2">
        <div>
          <label htmlFor="claimTopicDisplayName">Compliance Rule Display Name *</label>
          <div className="mt-3 ml-1 relative w-full flex border rounded-lg p-0">
            <Input
              id="claimTopicDisplayName"
              value={displayName}
              className="border w-full p-2 rounded-lg text-xl"
              placeholder="Enter Display Name"
              type="text"
              maxLength={32}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="absolute right-5 top-3">{displayName.length}/32</p>
          </div>
          <p>User-friendly name that describes the schema. Shown to end-users.</p>
        </div>
        <div className="mt-10 mb-6">
          <label htmlFor="claimTopicHiddenName">TopicID hidden name *</label>
          <div className="mt-3 ml-1 relative w-full flex border rounded-lg p-0">
            <Input
              id="claimTopicHiddenName"
              value={hiddenName ?? ""}
              readOnly
              className="border w-full p-2 rounded-lg text-xl"
              placeholder="Loading..."
              type="text"
              style={{ backgroundColor: "white", cursor: "default" }}
              onChange={(e) => setHiddenName(e.target.value)}
            />
          </div>
          <p>Only alphanumeric characters allowed and no spaces. Not seen by end-users.</p>
        </div>
        {!isReviewer && (
          <div className="flex justify-end max-[600px]:justify-center">
            <Button
              className="max-[600px]:w-[60%] min-w-max text-center font-semibold rounded h-11 bg-[#7F56D9] text-white"
              loading={saving}
              onClick={saveClaimTopic}
            >
              Create Compliance Rule
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateClaimTopic;
